const MINUTE = 60_000
const HOUR = 60 * MINUTE
const DAY = 24 * HOUR

// Constructing an Intl formatter dominates its cost, and these run once per
// table row and timeline entry, so keep one per locale.
const dateFormatters = new Map<string, Intl.DateTimeFormat>()
const dateTimeFormatters = new Map<string, Intl.DateTimeFormat>()
const relativeFormatters = new Map<string, Intl.RelativeTimeFormat>()

function cached<T>(cache: Map<string, T>, locale: string, create: () => T): T {
  let formatter = cache.get(locale)
  if (!formatter) {
    formatter = create()
    cache.set(locale, formatter)
  }
  return formatter
}

/**
 * SQLite `datetime('now')` returns "YYYY-MM-DD HH:MM:SS" in UTC with no zone
 * marker, which browsers would otherwise read as local time.
 */
export function parseApiDate(value: string): Date {
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(value)) {
    return new Date(`${value.replace(' ', 'T')}Z`)
  }
  return new Date(value)
}

export function formatDate(value: string | null | undefined, locale: string): string {
  if (!value) return '—'
  const formatter = cached(
    dateFormatters,
    locale,
    () =>
      new Intl.DateTimeFormat(locale, { day: '2-digit', month: 'short', year: 'numeric' }),
  )
  return formatter.format(parseApiDate(value))
}

export function formatDateTime(value: string | null | undefined, locale: string): string {
  if (!value) return '—'
  const formatter = cached(
    dateTimeFormatters,
    locale,
    () =>
      new Intl.DateTimeFormat(locale, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }),
  )
  return formatter.format(parseApiDate(value))
}

export function formatRelativeTime(value: string, locale: string): string {
  const date = parseApiDate(value)
  const diff = Date.now() - date.getTime()
  const formatter = cached(
    relativeFormatters,
    locale,
    () => new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }),
  )

  if (diff < HOUR) {
    return formatter.format(-Math.max(1, Math.round(diff / MINUTE)), 'minute')
  }
  if (diff < DAY) {
    return formatter.format(-Math.round(diff / HOUR), 'hour')
  }
  if (diff < 7 * DAY) {
    return formatter.format(-Math.round(diff / DAY), 'day')
  }
  return formatDate(value, locale)
}
